/**
 * /de/produktrecherche — product discovery concept page (German). Targets the
 * REAL keyword "produktrecherche" (90). Links /de/audio-transkribieren +
 * /de/prd-anforderungsdokument.
 */
import { MarketingShell } from './MarketingShell';

export function DeProduktrecherche() {
  return (
    <MarketingShell
      currentLocale="de"
      hreflangPath="/produktrecherche"
      hreflangLocales={['de']}
      documentTitle="Produktrecherche & Product Discovery: Methoden + Tools · Draftlize"
      documentDescription="Produktrecherche in der Praxis: Interview-Methoden, Insights synthetisieren und Interviews in nachvollziehbare Produktentscheidungen verwandeln."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Leitfaden · Discovery</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Produktrecherche (Discovery):<br />
            <em>vom Interview zur Entscheidung.</em>
          </h1>
          <p className="lede">
            Produktrecherche (Product Discovery) heißt: <em>was</em> gebaut werden soll zu prüfen, bevor es gebaut wird – über Interviews, Beobachtung und Validierung werden Annahmen zu Fakten. Selbst gut gemacht liegt die Schwachstelle immer an derselben Stelle: die <em>Insights gehen verloren, bevor sie zu Entscheidungen werden</em>. Hier die Methoden – und wie du Interviews in nachvollziehbare Entscheidungen verwandelst.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Methoden der Produktrecherche.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Nutzerinterviews</strong> – hör auf Problem und Kontext, nicht auf Antworten.</li>
            <li><strong>Beobachtung & Verhaltensdaten</strong> – schau, was sie tun, nicht nur, was sie sagen.</li>
            <li><strong>Validierung (Prototypen, Experimente)</strong> – prüf Hypothesen im Kleinen.</li>
            <li><strong>Synthese</strong> – verdichte verstreute Funde zu Mustern, die für eine Entscheidung reichen.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Beginne mit dem <a href="/de/audio-transkribieren" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>Transkribieren des Interviews</a> als Rohmaterial. Aber das Transkript ist nicht das Ziel – es ist der Startpunkt für Synthese und Entscheidung.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Die Schwachstelle: zwischen Fund und Entscheidung. <em>Verbinde sie.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Das Ergebnis der Produktrecherche sind nicht die Sätze aus den Interviews, sondern die <em>Entscheidungen</em>, die du daraus ableitest. Viele Teams fassen Funde in einer Präsentation zusammen – und dort endet es: Entscheidung und Begründung leben nirgends. Draftlize verknüpft „Zitat → Entscheidung → Anforderung“.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Warum Insights verloren gehen</h3>
            <p>Die Highlights des Interviews verstreuen sich über Notion-Seiten oder Protokolle, und im nächsten Sprint liest sie niemand mehr – weil das „Warum“ der Entscheidung vom Fund abgeschnitten ist.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Verknüpfe „Zitat → Entscheidung → Anforderung“</h3>
            <p>In Draftlize verknüpfst du den Satz eines Nutzers mit der Entscheidung, die er ausgelöst hat, und der Anforderung, die daraus folgt. Kippt eine spätere Validierung die Annahme, wird das Abhängige „stale“.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Dein KI-Agent baut auf den Funden auf</h3>
            <p>Die Discovery-Entscheidungen werden Kontext, den Claude Code oder Cursor via MCP lesen. Die nächste Spec entsteht mit dem Wissen „der Nutzer sagte dies, also entschieden wir jenes“. Weiter zum <a href="/de/prd-anforderungsdokument" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Der Wert der Produktrecherche liegt nicht in der Zahl gesammelter Insights, sondern darin, ob du sechs Monate später die damit getroffenen Entscheidungen noch nachvollziehen kannst.
        </blockquote>
        <cite>Verbinde den Fund mit der Entscheidung und melde, wenn die Annahme sich ändert. Das ist Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Häufige Fragen.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Discovery oder Delivery?', a: 'Discovery prüft „das Richtige bauen“; Delivery heißt „es richtig bauen“. Sie gehören zusammen – aber wenn die Discovery-Entscheidungen nicht bleiben, zielt das Delivery daneben.' },
            { q: 'Wie viele Interviews?', a: 'Weniger eine feste Zahl als: bis keine neuen Funde mehr auftauchen (Sättigung). Wichtig ist nicht die Menge, sondern mit welcher Entscheidung du jeden Satz verknüpft hast.' },
            { q: 'Wie synthetisiere ich die Funde?', a: 'Gruppiere die Sätze nach Themen und suche Muster, die für eine Entscheidung reichen. In Draftlize wird jedes Muster zur Entscheidungskarte, verknüpft mit den Original-Sätzen.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Verknüpfe jeden Interview-Satz mit der Entscheidung <em>– gratis mit $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>$5 Gratis-Guthaben für neue Accounts</span>
          <span>Zahle nur, was du nutzt</span>
          <span>Guthaben verfällt nie</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Verwandle die Funde der Produktrecherche in „Zitat → Entscheidung → Anforderung“-Karten in Draftlize. Ändert sich die Annahme, wird sie von selbst „stale“, und dein KI-Agent liest sie.
          </p>
          <a className="btn-primary" href="/">Gratis mit $5 starten</a>
        </div>
      </section>
    </MarketingShell>
  );
}
